import {
  InteractiveContainer,
  Menu,
  PlainButton,
} from "@jugl-web/ui-components";
import { Switch } from "@jugl-web/ui-components/cross-platform/Switch";
import { useTranslations } from "@jugl-web/utils";
import type { CustomTemplateData } from "@jugl-web/rest-api";
import React from "react";
import { ReactComponent as CreateNewIcon } from "../../assets/create-new.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as TrashIcon } from "./assets/trash.svg";

export interface CustomToolCardProps {
  tool: CustomTemplateData;
  onEdit: () => void;
  onDelete: () => void;
  onToggleEnabled: (isEnabled: boolean) => void;
  isToggleDisabled?: boolean;
}

export const CustomToolCard: React.FC<CustomToolCardProps> = ({
  tool,
  onEdit,
  onDelete,
  onToggleEnabled,
  isToggleDisabled = false,
}) => {
  const { t } = useTranslations();

  const title =
    tool.name?.trim().replace(/_/g, " ") ||
    t({
      id: "cx-actions-page.custom-tool-empty-name",
      defaultMessage: "Untitled tool",
    });
  const subtitle = tool.url
    ? `${tool.method} ${tool.url}`
    : (tool.description ?? "");

  return (
    <InteractiveContainer
      onClick={onEdit}
      className="flex w-full max-w-[480px] min-w-[440px] items-stretch gap-4 rounded-xl border border-solid border-dark-100 bg-white p-5 transition-colors hover:border-dashed hover:border-primary-200"
    >
      <div
        className="flex h-[136px] w-[100px] shrink-0 items-center justify-center rounded-xl"
        style={{
          backgroundImage:
            "radial-gradient(circle, #E1E4E6 1px, #FCFCFC 1.5px)",
          backgroundSize: "8px 8px",
        }}
      >
        <CreateNewIcon />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-between">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate font-secondary text-lg leading-[27px] font-semibold text-black">
              {title}
            </span>
            <div
              className="flex shrink-0 items-center gap-2"
              onClick={(event) => event.stopPropagation()}
              role="presentation"
            >
              <Switch
                isChecked={tool.enabled}
                isDisabled={isToggleDisabled}
                onChange={onToggleEnabled}
              />
              <Menu
                autoClose
                placement="bottom"
                renderTrigger={({ Trigger, triggerRef, isOpen }) => (
                  <Trigger
                    ref={triggerRef}
                    as={PlainButton}
                    onClick={(event: React.MouseEvent) =>
                      event.stopPropagation()
                    }
                    className={
                      isOpen
                        ? "flex h-8 w-8 items-center justify-center rounded-[10px] border border-solid border-[#F2F2F2] ring-1 ring-[#F2F2F2] ring-inset"
                        : "flex h-8 w-8 items-center justify-center rounded-[10px] border border-solid border-[#F2F2F2] ring-[#F2F2F2] transition ring-inset hover:ring-1"
                    }
                  >
                    <MoreIcon />
                  </Trigger>
                )}
                sections={[
                  [
                    {
                      id: "edit",
                      label: t({ id: "common.edit", defaultMessage: "Edit" }),
                      onSelect: onEdit,
                    },
                    {
                      id: "delete",
                      label: t({
                        id: "common.delete",
                        defaultMessage: "Delete",
                      }),
                      icon: <TrashIcon />,
                      onSelect: onDelete,
                    },
                  ],
                ]}
              />
            </div>
          </div>
          {subtitle && (
            <span className="truncate font-secondary text-sm leading-[21px] text-[#737B7E]">
              {subtitle}
            </span>
          )}
        </div>
      </div>
    </InteractiveContainer>
  );
};
